import { Navigate } from "react-router-dom";
import { useEffect, useState, type ReactNode } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useAuth } from "@/context/AuthContext";
import { authFetch } from "@/lib/auth";
import {
  signupConsentSchema,
  SIGNUP_CONSENT_DRAFT,
} from "@shared/signupConsent";
import {
  SignupConsentFields,
  EMPTY_CONSENT,
  consentPayload,
} from "./SignupConsentFields";
import AuthShell from "./AuthShell";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";

export default function SignupCompletionGate({
  children,
  showForm = false,
}: {
  children: ReactNode;
  showForm?: boolean;
}) {
  const { user, session, isLoading, authReady, logout } = useAuth();
  const client = useQueryClient();
  const key = ["signup-consent", user?.id];
  const [consent, setConsent] = useState(EMPTY_CONSENT);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const status = useQuery({
    queryKey: key,
    enabled: !!session && session.user.id === user?.id,
    // A server-confirmed result lasts for this login; logout/account changes clear the query cache.
    staleTime: Infinity,
    gcTime: Infinity,
    retry: 1,
    queryFn: async ({ signal }) => {
      // Auth already supplied this session. Asking Supabase for it again can wait on its auth lock.
      const response = await fetch(`${import.meta.env.VITE_API_URL || ""}/api/auth/registration`, {
        headers: { Authorization: `Bearer ${session!.access_token}` },
        signal: AbortSignal.any([signal, AbortSignal.timeout(10_000)]),
      });
      const data = await response.json();
      if (!response.ok || typeof data.required !== "boolean")
        throw new Error("Could not check your signup details.");
      return data as { required: boolean };
    },
  });
  async function save(data: unknown) {
    const valid = signupConsentSchema.safeParse(data);
    if (!valid.success) {
      setError(valid.error.issues[0].message);
      return;
    }
    setSaving(true);
    setError("");
    try {
      const response = await authFetch("/api/auth/registration", {
        method: "POST",
        body: JSON.stringify(valid.data),
      });
      if (!response.ok)
        throw new Error("Your details could not be saved. Please retry.");
      try {
        sessionStorage.removeItem(SIGNUP_CONSENT_DRAFT);
      } catch {
        /* storage unavailable */
      }
      client.setQueryData(key, { required: false });
      void client.invalidateQueries({
        predicate: (query) => query.queryKey[0] !== "signup-consent",
      });
    } catch (e) {
      setError(e instanceof Error ? e.message : "Please try again.");
    } finally {
      setSaving(false);
    }
  }
  useEffect(() => {
    if (!status.data || (status.data.required && !showForm)) return;
    try {
      const raw = sessionStorage.getItem(SIGNUP_CONSENT_DRAFT);
      sessionStorage.removeItem(SIGNUP_CONSENT_DRAFT);
      if (!status.data.required || !raw) return;
      const pending = JSON.parse(raw);
      const valid = signupConsentSchema.safeParse(pending.data);
      if (pending.expires > Date.now() && valid.success) {
        setConsent({
          month: String(valid.data.birth_month),
          year: String(valid.data.birth_year),
          accepted: true,
        });
        void save(valid.data);
      }
    } catch {
      /* Ask explicitly again when the saved intent is missing or invalid. */
    }
  }, [status.data?.required, user?.id, showForm]);
  if (isLoading || !authReady) return null;
  if (!session) return <Navigate to="/login" replace />;
  if (status.isPending)
    return (
      <div role="status" aria-label="Loading your study space" className="mx-auto w-full max-w-5xl space-y-6 p-5 sm:p-8">
        <span className="sr-only">Loading your study space</span>
        <Skeleton className="h-8 w-2/3 max-w-sm" />
        <Skeleton className="h-4 w-1/2 max-w-xs" />
        <Skeleton className="h-40 w-full rounded-2xl" />
        <div className="grid gap-4 sm:grid-cols-2">
          <Skeleton className="h-48 rounded-2xl" />
          <Skeleton className="h-48 rounded-2xl" />
        </div>
      </div>
    );
  if (status.isError)
    return (
      <AuthShell>
        <div role="alert" className="space-y-4">
          <h1 className="text-section text-ink">
            We could not check your signup details
          </h1>
          <Button onClick={() => void status.refetch()}>Try again</Button>
          <Button variant="ghost" onClick={() => void logout()}>
            Sign out
          </Button>
        </div>
      </AuthShell>
    );
  if (!status.data?.required) return <>{children}</>;
  if (!showForm) return <Navigate to="/complete-signup" replace />;
  return (
    <AuthShell>
      <form
        className="space-y-5"
        onSubmit={(e) => {
          e.preventDefault();
          if (!saving) void save(consentPayload(consent));
        }}
      >
        <h1 className="text-section text-ink">Finish creating your account</h1>
        <p className="text-sm text-ink-2">
          Before you start studying, add your birth month and year and review
          our privacy agreement.
        </p>
        <SignupConsentFields
          value={consent}
          onChange={setConsent}
          disabled={saving}
        />
        {error && (
          <p role="alert" className="text-sm text-danger">
            {error}
          </p>
        )}
        <Button
          type="submit"
          className="w-full"
          disabled={
            saving ||
            !signupConsentSchema.safeParse(consentPayload(consent)).success
          }
        >
          {saving ? "Saving…" : "Continue to setup"}
        </Button>
        <Button
          type="button"
          variant="ghost"
          className="w-full"
          onClick={() => void logout()}
        >
          Sign out
        </Button>
      </form>
    </AuthShell>
  );
}
